import {
  MARK_LANES,
  MARK_Z_END,
  MARK_Z_START,
  WAVE_SPEED,
  laneMarkPath,
} from './geometry'

/* =========================================
   FAUX PRINT — blurred text-like marks
   The incoming paper (right of the block) is covered in rows of
   soft, blurred pill shapes of varied length — reads as dense print
   without being legible text. Each mark is a tiny <rect rx> whose
   transform is recomputed every frame (translate onto the wavy
   surface + rotate to the local tangent), so the "print" undulates
   with the paper and dives into the block with it.

   The marks FLOW along the belt at exactly the wave's travel speed,
   so they ride the paper like ink. They are drawn only on the
   page-fixed incoming strip (BeltStrip): the block and the outgoing
   surfaces are painted over it, so the marks vanish into the block
   and the paper comes out the other side clean.
   ========================================= */

export const MARK_FILL = '#e5e1d9'
export const MARK_H = 11 // pill height (px) — the stroke width of the mark path
const MARK_GAP = 30 // fixed gap between pills, identical in every row (px)
/* px/s the print travels — identical to the wave, so ink stays put
   on the paper (100 screen px per z unit). */
export const MARK_FLOW_SPEED = WAVE_SPEED * 100

export interface Mark {
  lane: number
  s: number // start along its lane (px of material)
  len: number // pill length (px)
}

export interface RunMarks {
  marks: Mark[]
  laneLen: number // material length of a lane; marks wrap modulo this
}

/* Deterministic PRNG (mulberry32) — layout must match between server
   render and client hydration, so no Math.random(). */
function rng(seed: number) {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function buildRunMarks(): RunMarks {
  const laneLen = (MARK_Z_END - MARK_Z_START) * 100
  const marks: Mark[] = []
  MARK_LANES.forEach((_, lane) => {
    const rand = rng(lane * 7919 + 17)
    /* Draw varied lengths until the lane is roughly full... */
    const lens: number[] = []
    let total = 0
    while (total + MARK_GAP < laneLen) {
      const len = 16 + rand() * 112 // strongly varied lengths
      lens.push(len)
      total += len + MARK_GAP
    }
    /* ...then scale them so the pattern tiles the loop EXACTLY:
       every gap — including the one at the wrap seam — is MARK_GAP,
       so spacing is uniform within and across all rows. */
    const gaps = lens.length * MARK_GAP
    const scale = (laneLen - gaps) / (total - gaps)
    let s = rand() * laneLen // stagger the rows against each other
    for (const raw of lens) {
      const len = raw * scale
      marks.push({ lane, s: s % laneLen, len })
      s += len + MARK_GAP
    }
  })
  return { marks, laneLen }
}

/* Path for one mark at the current flow offset (px): a stretch of
   the lane's wavy surface line, drawn as a thick round-capped stroke
   so the pill BENDS with the paper. Material position is measured
   from the FOLD end back toward the entry, so advancing the flow
   moves every mark toward +z (into the block). */
export function markPath(
  m: Mark,
  turnZ: number,
  laneLen: number,
  phase: number,
  travel: number,
  flow: number
): string {
  const sEff = (((m.s - flow) % laneLen) + laneLen) % laneLen
  const zHigh = MARK_Z_START + (laneLen - sEff) / 100
  const zLow = Math.max(MARK_Z_START, zHigh - m.len / 100)
  return laneMarkPath(MARK_LANES[m.lane], zLow, zHigh, turnZ, phase, travel)
}
